/*
 * Obermaier — Handtuchfinder, loader (sections/handtuchfinder.liquid)
 * The small part that runs on every visit of the collection: opens the panel for links to
 * #handtuchfinder, adds the finder chip to the subcategory bar, keeps the browser back button in step
 * with the panel and loads the finder itself (assets/handtuchfinder-app.js) on the first sign of interest.
 */
import { openSheet, define } from '@theme/core';

const ID = 'handtuchfinder';
const STATE_KEY = 'omTowelFinder';
// Compare paths without the language folder (/en/, /fr/, /it/), so a link from another language works too.
const bare = (path) => path.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i, '');
const isFinderLink = (a) => !!a && a.hash === `#${ID}` && bare(a.pathname) === bare(location.pathname);
const ours = () => history.state?.[STATE_KEY] === true;
const urlWith = (hash) => location.pathname + location.search + hash;
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const CHIP_ICON = '<svg class="om-icon om-icon--sm" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 2.5 L11.6 7.4 L16.5 9 L11.6 10.6 L10 15.5 L8.4 10.6 L3.5 9 L8.4 7.4 Z"/><path d="M15.5 13.5 L16 15 L17.5 15.5 L16 16 L15.5 17.5 L15 16 L13.5 15.5 L15 15 Z"/></svg>';

class TowelFinderShell extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.dialog.addEventListener('om:sheet:open', () => this.start());
    this.dialog.addEventListener('close', () => this.onClose());
    this.addChip();
  }

  /** Loads the finder module once; it starts fetching the product data right away. */
  load() {
    if (!this.app) {
      this.app = import(this.dataset.app)
        .then((module) => module.mount(this))
        .catch((error) => { this.app = null; throw error; });
    }
    return this.app;
  }

  start() {
    this.load().then((app) => app.opened()).catch(() => {
      const body = this.querySelector('[data-finder-body]');
      if (body) body.innerHTML = `<p class="om-muted">${esc(this.dataset.error)}</p>`;
    });
  }

  /** Opens the panel and adds a history entry, so the back button closes it instead of leaving the page. */
  open(opener) {
    if (!ours()) history.pushState({ ...(history.state || {}), [STATE_KEY]: true }, '', urlWith(`#${ID}`));
    openSheet(ID, opener || document.activeElement);
  }

  onClose() {
    if (ours()) history.back();
    else if (location.hash === `#${ID}`) history.replaceState(history.state, '', urlWith(''));
  }

  /** Chip next to the current one ("Handtücher"), so it is in view without scrolling on phones. */
  addChip() {
    const list = document.querySelector('[data-om-subnav-list]');
    if (!list || list.querySelector('[data-finder-chip]')) return;
    const li = document.createElement('li');
    li.setAttribute('data-finder-chip', '');
    li.innerHTML = `<a class="om-chip om-subnav__chip om-finder-chip" href="#${ID}">${CHIP_ICON}<span>${esc(this.dataset.chip)}</span></a>`;
    const current = list.querySelector('[aria-current="page"]')?.closest('li');
    if (current) current.after(li); else list.append(li);
    // Desktop: the bar collapses to two rows; count the new chip in (assets/collection.js).
    list.closest('om-collection')?.layoutSubnav?.();
  }
}
define('om-towel-finder', TowelFinderShell);

const shell = () => document.querySelector('om-towel-finder');

document.addEventListener('click', (event) => {
  const link = event.target.closest?.('a[href*="#handtuchfinder"]');
  if (!isFinderLink(link) || !shell() || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  shell().open(link);
});

// First sign of interest: load the finder and its data before the click.
const prefetch = (event) => {
  if (isFinderLink(event.target.closest?.('a[href*="#handtuchfinder"]'))) shell()?.load().catch(() => {});
};
document.addEventListener('pointerover', prefetch, { passive: true });
document.addEventListener('focusin', prefetch);
document.addEventListener('touchstart', prefetch, { passive: true });

// Browser back while the panel is open: close it (the history entry is already gone).
addEventListener('popstate', () => {
  const dialog = document.getElementById(ID);
  if (dialog?.open && !ours()) dialog.close();
});

// A link from elsewhere (product page, newsletter) lands with the hash: open right away. The plain URL
// stays in the history underneath, so closing the panel or pressing back keeps the visitor on the page.
const openFromHash = () => {
  if (location.hash !== `#${ID}` || !shell() || document.getElementById(ID)?.open) return;
  history.replaceState(history.state, '', urlWith(''));
  shell().open();
};
addEventListener('hashchange', openFromHash);
requestAnimationFrame(openFromHash);

/* Theme editor: show the panel while its section is selected. */
document.addEventListener('shopify:section:select', (event) => {
  if (event.target.querySelector?.(`#${ID}`)) openSheet(ID);
});
document.addEventListener('shopify:section:deselect', (event) => {
  if (event.target.querySelector?.(`#${ID}`)) document.getElementById(ID)?.close();
});
